import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPrinterById, createPrinter, updatePrinter, getPrintAgents } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function AddEditPrinter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("printers");
  const { t: tCommon } = useTranslation("common");

  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({
    name: "",
    print_agent_id: "",
    host: "",
    port: "9100",
    is_default: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getPrintAgents(token).then((data) => setAgents(data || [])).catch(() => notify.error(t("notify_load_agents_failed", "Failed to load print agents")));
    if (id) {
      getPrinterById(token, id).then((p) => {
        setForm({
          name: p.name || "",
          print_agent_id: p.print_agent_id ? String(p.print_agent_id) : "",
          host: p.host || "",
          port: String(p.port || 9100),
          is_default: !!p.is_default,
        });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load printer")));
    }
  }, [id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const agentOptions = useMemo(
    () => agents.map((a) => ({ value: String(a.id), label: `${a.name} (${a.branch?.name || "—"})` })),
    [agents]
  );

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Printer name is required");
    if (!form.print_agent_id) newErrors.print_agent_id = t("validation_agent_required", "A print agent is required");
    if (!form.host.trim()) newErrors.host = t("validation_host_required", "Host/IP address is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      print_agent_id: Number(form.print_agent_id),
      host: form.host.trim(),
      port: Number(form.port) || 9100,
      is_default: form.is_default,
    };

    try {
      if (id) {
        await updatePrinter(token, id, payload);
        notify.success(t("notify_updated", "Printer updated successfully"));
      } else {
        await createPrinter(token, payload);
        notify.success(t("notify_created", "Printer created successfully"));
      }
      navigate("/printers");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/printers")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Printer") : t("add_title", "Add New Printer")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("form_subtitle", "A network thermal printer — raw ESC/POS bytes are sent to this address on port 9100 by default")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <FloatingInput label={t("field_name_label", "Printer Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Printers} />

          <SelectBox
            label={t("field_agent_label", "Print Agent")}
            options={agentOptions}
            value={form.print_agent_id}
            onChange={(val) => { setForm((prev) => ({ ...prev, print_agent_id: val })); if (errors.print_agent_id) setErrors((prev) => ({ ...prev, print_agent_id: undefined })); }}
            error={errors.print_agent_id}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FloatingInput label={t("field_host_label", "Host / IP Address")} value={form.host} onChange={setField("host")} error={errors.host} />
            <FloatingInput label={t("field_port_label", "Port")} type="number" value={form.port} onChange={setField("port")} />
          </div>

          <ToggleSwitch
            label={t("field_is_default_label", "Default printer for this branch")}
            checked={form.is_default}
            onChange={(val) => setForm((prev) => ({ ...prev, is_default: val }))}
          />

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={id ? ICONS.Save : ICONS.Plus}
            >
              {id ? t("btn_update_printer", "Update Printer") : t("btn_create_printer", "Create Printer")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/printers")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
